import type { RentEstimateDetailModel } from 'src/api/models';
import { StringComponent } from 'src/features/faqs/components/string/String';
import { STRINGS } from './strings';
import type { FeaturesStepFormSchema, FlowData, Step, StepId } from './types';

export const STEPS: Record<StepId, Step> = {
  account: {
    active: false,
    id: 'account',
    label: STRINGS.account.label,
  },
  home: {
    active: false,
    id: 'home',
    label: STRINGS.home.label,
  },
  expectation: {
    active: false,
    id: 'expectation',
    label: STRINGS.expectation.label,
  },
  condition: {
    active: false,
    id: 'condition',
    label: STRINGS.condition.label,
  },
  pets: {
    active: false,
    id: 'pets',
    label: STRINGS.pets.label,
  },
  primaryMaterial: {
    active: false,
    id: 'primaryMaterial',
    label: STRINGS.primaryMaterial.label,
  },
  features: {
    active: false,
    id: 'features',
    label: STRINGS.features.label,
  },
};

export const LOCAL_STORAGE_KEY = 'rent-estimate';

export const QUERY_PARAMS = {
  ADDRESS: 'address',
  HOME_ID: 'homeId',
  OUT_OF_COVERAGE: 'outOfCoverage',
};

export const INITIAL_FLOW_DATA: FlowData = {
  createdOn: '',
  data: {
    account: {
      hasBelongAccount: null,
    },
    condition: {
      lastRenovation: null,
    },
    expectation: {
      hasExpectations: null,
      expectedRent: null,
    },
    home: {
      // @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56
      address: {
        city: '',
        streetAddress: '',
        state: '',
        unitNumber: '',
        zipcode: '',
        displayNeighborhood: false,
        outsideUS: false,
      },
    },
    pets: {
      allowPets: null,
    },
    primaryMaterial: {
      primaryMaterial: null,
    },
    features: {
      'Garage Parking': false,
      Pool: false,
      Laundry: false,
      none: false,
    },
  },
};

export const ACCOUNT_FORM_HAS_BELONG_ACCOUNT_OPTIONS = [
  {
    label: STRINGS.account.yes,
    value: true,
  },
  {
    label: STRINGS.account.no,
    value: false,
  },
];

export const HOME_OUT_OF_COVERAGE_FORM_OPTIONS = [
  {
    label: STRINGS.home.outOfCoverageYesLabel,
    subLabel: STRINGS.home.outOfCoverageYesSubLabel,
    value: true,
  },
  {
    label: STRINGS.home.outOfCoverageNoLabel,
    subLabel: STRINGS.home.outOfCoverageNoSubLabel,
    value: false,
  },
];

export const EXPECTATIONS_FORM_OPTIONS = [
  {
    label: STRINGS.expectation.yes,
    subLabel: STRINGS.expectation.yesDetail,
    value: true,
  },
  {
    label: STRINGS.expectation.no,
    subLabel: STRINGS.expectation.noDetail,
    value: false,
  },
];

export const CONDITION_FORM_LAST_RENOVATION_OPTIONS = [
  {
    label: STRINGS.condition.pastYear,
    value: 'PastYear',
    hint: STRINGS.condition.info.PastYear,
  },
  {
    label: STRINGS.condition.oneThreeYears,
    value: 'OneToThree',
    hint: STRINGS.condition.info.OneToThree,
  },
  {
    label: STRINGS.condition.threeFiveYears,
    value: 'ThreeToFive',
    hint: STRINGS.condition.info.ThreeToFive,
  },
  {
    label: STRINGS.condition.plusFiveYears,
    value: 'MoreThanFive',
    hint: STRINGS.condition.info.MoreThanFive,
  },
];

export const PETS_FORM_ALLOW_PETS_OPTIONS = [
  {
    label: STRINGS.pets.yes,
    value: true,
    iconNormal: '/icons/rent-estimates/ic_pet_yes_normal.svg',
    iconActive: '/icons/rent-estimates/ic_pet_yes_active.svg',
  },
  {
    label: STRINGS.pets.no,
    value: false,
    iconNormal: '/icons/rent-estimates/ic_pet_no_normal.svg',
    iconActive: '/icons/rent-estimates/ic_pet_no_active.svg',
  },
];

export const PRIMARY_MATERIAL_FORM_PRIMARY_MATERIAL_OPTIONS = [
  {
    label: STRINGS.primaryMaterial.stainlessSteel,
    value: 'StainlessSteel',
    icon: '/rent-estimate/stainless-steel.png',
    hint: STRINGS.primaryMaterial.info.StainlessSteel,
  },
  {
    label: STRINGS.primaryMaterial.coloredFinish,
    value: 'ColoredFinish',
    icon: '/rent-estimate/black-finish.png',
    hint: STRINGS.primaryMaterial.info.ColoredFinish,
  },
  {
    label: STRINGS.primaryMaterial.customFinish,
    value: 'CustomFinish',
    icon: '/rent-estimate/custom-finish.png',
    hint: STRINGS.primaryMaterial.info.CustomFinish,
  },
];

export const FEATURES_OPTIONS: {
  label: string;
  value: keyof FeaturesStepFormSchema;
}[] = [
  {
    label: STRINGS.features.options.Pool,
    value: 'Pool',
  },
  {
    label: STRINGS.features.options.Laundry,
    value: 'Laundry',
  },
  {
    label: STRINGS.features.options['Garage Parking'],
    value: 'Garage Parking',
  },
  {
    label: STRINGS.features.options.none,
    value: 'none',
  },
];

export const DEFAULT_HOMEOWNER_ADVISOR = {
  name: 'Sparsh',
  avatarSrc:
    'https://s3-us-west-1.amazonaws.com/bln-prd-usr-pics/47148-ee4f5163b8eee4a3',
  role: 'Pricing Expert',
};

export const GA_EXPERIMENT = {
  NAPKING_MODAL: 'napkin_modal',
  RENT_ESTIMATE: 'rent_estimate',
};

export const GA_EVENTS = {
  FLOW_FINISHED: 'rent_estimate_flow_finished',
  FLOW_STARTED: 'rent_estimate_flow_started',
};

export const GA_ENTRY_POINTS = {
  HO_ADDRESS_INPUT: 'ho_address_input',
  HO_LANDING_BANNER_CTA: 'ho_landing_banner_cta',
  HO_STICKY_NAV_BAR: 'ho_sticky_nav_bar',
};

export const VALUE_PROPOSITIONS = [
  {
    imageSrc: '/rent-estimate/advanced-data-precision.svg',
    title: STRINGS.landing.valuePropsDataPrecisionTitle,
    description: STRINGS.landing.valuePropsDataPrecisionDescription,
  },
  {
    imageSrc: '/rent-estimate/rental-valuation.svg',
    title: STRINGS.landing.valuePropsRentalValuationTitle,
    description: STRINGS.landing.valuePropsRentalValuationDescription,
  },
  {
    imageSrc: '/rent-estimate/market-intelligence.svg',
    title: STRINGS.landing.valuePropsMarketIntelligenceTitle,
    description: STRINGS.landing.valuePropsMarketIntelligenceDescription,
  },
];

export const SOCIAL_PROOF_OPINIONS = [
  {
    src: '/logos/businessinsider_logo_dark_gray.svg',
    alt: STRINGS.landing.socialProofBusinessInsiderLogo,
    body: STRINGS.landing.socialProofBusinessInsider,
  },
  {
    src: '/logos/techcrunch_logo_dark_gray.svg',
    alt: STRINGS.landing.socialProofTechCrunchLogo,
    body: STRINGS.landing.socialProofTechCrunch,
  },
  {
    src: '/logos/sfist_logo_dark_gray.svg',
    alt: STRINGS.landing.socialProofSFistLogo,
    body: STRINGS.landing.socialProofSFist,
  },
  {
    src: '/logos/cbinsights_dark_gray.svg',
    alt: STRINGS.landing.socialProofCBInsightsLogo,
    body: STRINGS.landing.socialProofCBInsights,
  },
];

export const SOCIAL_PROOF_STATS = [
  {
    count: `50,000+`,
    label: STRINGS.landing.socialProofRentEstimatesDelivered,
  },
  {
    count: '10,000+',
    label: STRINGS.landing.socialProofMembers,
  },
  {
    rating: '4.7',
    label: STRINGS.landing.socialProofGoogle,
  },
  {
    rating: '4.9',
    label: STRINGS.landing.socialProofThumbtack,
  },
];

const FAQ_ITEMS = [
  {
    question: STRINGS.landing.faqRentalPricing,
    answer: STRINGS.landing.faqRentalPricingAnswer,
  },
  {
    question: STRINGS.landing.faqRentEstimate,
    answer: STRINGS.landing.faqRentEstimateAnswer,
  },
  {
    question: STRINGS.landing.faqBelongAccuracy,
    answer: STRINGS.landing.faqBelongAccuracyAnswer,
  },
  {
    question: STRINGS.landing.faqRentEstimateFactors,
    answer: STRINGS.landing.faqRentEstimateFactorsAnswer,
  },
  {
    question: STRINGS.landing.faqValueIncrease,
    answer: STRINGS.landing.faqValueIncreaseAnswer,
  },
  {
    question: STRINGS.landing.faqPropertyTypes,
    answer: STRINGS.landing.faqPropertyTypesAnswer,
  },
];

export const FAQ_ACCORDION_ITEMS = FAQ_ITEMS.map((faqItem) => {
  return {
    title: faqItem.question,
    content: <StringComponent string={faqItem.answer} />,
  };
});

export const FAQ_STRUCTURED_ITEMS = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((faqItem) => {
    return {
      '@type': 'Question',
      name: faqItem.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.answer,
      },
    };
  }),
};

export const REPORT_SUMMARY_EXAMPLE: RentEstimateDetailModel = {
  rentRange: {
    priceLowerBound: 3020,
    priceUpperBound: 3620,
    priceMean: 3320,
  },
  hasReportData: true,
};

export const OPEN_GRAPH = {
  title: STRINGS.landing.title,
  description: STRINGS.landing.description,
};

export const ADDITIONAL_META_TAGS = [
  {
    name: 'keywords',
    content: STRINGS.landing.keywords,
  },
];
